import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Button, Grid, Tooltip } from '@mui/material';
import * as assetApi from '../../api/app/assetApi';
import * as dashboardApi from '../../api/app/dashboardApi';
import { Loader } from '../../components/core/Loader/Loader';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { Asset } from '../../types/app/asset.types';
import { DataGridTable } from '../../components/core/DataGridTable/DataGridTable';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../utils/core/routes';
import { DashboardOfAsset, DashboardTypes } from '../../types/app/dashboard.types';
import { AssetListGroupSearch } from './AssetListGroupSearch';
import { AssetDashboardMenu } from './AssetDashboardMenu';
import { useTranslation } from '../../contexts/translation';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { STORAGE_KEYS } from '../../utils/core/localStorageKeys';
import { useSessionStorage } from '../../hooks/useSessionStorage';

export function AssetList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [refetch, setRefetch] = useState<boolean>(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | undefined>();
  const [selectedGroup, setSelectedGroup] = useState<number>();
  const [storageGroup, setStorageGroup] = useLocalStorage(STORAGE_KEYS.ASSET_GROUP, selectedGroup);
  const [localData, setLocalData] = useState<Asset[]>();
  const [openAssetsWithDescendantsArr, setOpenAssetsWithDescendantsArr] = useState<number[]>([]);
  const [openAssetsWithDescendants, setOpenAssetsWithDescendants] = useSessionStorage(
    STORAGE_KEYS.ASSET_LIST_OPEN_ARR,
    [],
  );

  useEffect(() => {
    setOpenAssetsWithDescendantsArr(Object.values(openAssetsWithDescendants));
  }, []);

  // Načte seznam assetů
  const { data, isLoading, isError } = useQuery<Asset[]>(['assets', selectedGroup], () =>
    assetApi.getListWithDashboardsFiltered(storageGroup ? storageGroup : selectedGroup),
  );

  // Načte dashboardy k assetu
  const { data: dashboardData, isFetching: dashboardDataFetching } = useQuery<DashboardOfAsset[]>(
    ['assetDashboards'],
    () => dashboardApi.getDashboardList(selectedAsset?.id),
    {
      enabled: refetch,
      onSuccess: () => {
        setRefetch(false);
      },
    },
  );

  // Při změně skupiny přenačteme assety
  useEffect(() => {
    queryClient.invalidateQueries(['assets']);
  }, [selectedGroup]);

  // Při načtení dat nastavíme lokální data. Nad lokálními daty pracuje groupování.
  useEffect(() => {
    if (!data) return;
    let _data = data;

    _data.forEach((item) => {
      // Pokud nemá parenta, jedná se o nejvyšší úroveň
      if (!item.parentAssetId) {
        item.level = 0;
        return;
      }

      let parentAssetId = item.parentAssetId;
      let counter = 0;
      // Má parenta - je nutné se rekruzivně podívat, jestli má parent parenta
      while (parentAssetId > 0) {
        const parent = _data.filter((x) => x.id === parentAssetId)[0];
        if (parent) {
          counter++;
          parentAssetId = parent.parentAssetId;
        } else parentAssetId = 0;
      }

      item.level = counter;
    });

    setLocalData(_data.filter((x) => x.parentAssetId === null));
    setOpenAssetsWithDescendantsArr(Object.values(openAssetsWithDescendants));
  }, [data]);

  // Při změně otveřených assetů (grouping), profiltrujeme localData
  useEffect(() => {
    if (!data) return;
    const _localData = data.filter(
      (x) => x.parentAssetId === null || openAssetsWithDescendantsArr.includes(x.parentAssetId),
    );

    setLocalData(_localData);
  }, [openAssetsWithDescendantsArr]);

  // Otevřeme asset modal s tlačítky
  const handleOpen = (asset: any) => {
    setSelectedAsset(asset);
    setRefetch(true);
    setOpen(true);
  };

  const isAssetOpen = (id: number) =>
    openAssetsWithDescendantsArr.filter((x) => x === id).length > 0;

  const getDescendantsIds = (data: Asset[], assetId: number) => {
    let ids: number[] = [];
    data
      .filter((x) => x.parentAssetId === assetId)
      .forEach((item) => {
        ids = [...ids, item.id];
        data
          .filter((y) => y.parentAssetId === item.id)
          .forEach((item2) => {
            ids = [...ids, item2.id];
            const descendantsIds = getDescendantsIds(data, item2.id);
            ids = [...ids, ...descendantsIds];
          });
      });

    return ids;
  };

  // Přidá nebo odebere asset ze seznamu otevřených assetů (grouping)
  const handleGrouping = (asset: any) => {
    if (!data || !localData) return;

    const isOpen = isAssetOpen(asset.id);
    let _openAssetsWithDescendantsArr: number[] = [];
    if (isOpen) {
      const toRemoveIds = getDescendantsIds(localData, asset.id);
      _openAssetsWithDescendantsArr = openAssetsWithDescendantsArr.filter(
        (x) => x !== asset.id && !toRemoveIds.includes(x),
      );
    } else _openAssetsWithDescendantsArr = [...openAssetsWithDescendantsArr, asset.id];

    setOpenAssetsWithDescendants(_openAssetsWithDescendantsArr);
    setOpenAssetsWithDescendantsArr(_openAssetsWithDescendantsArr);
  };

  // Handle kliknutí na dashboard assetu
  const handleAssetButtonActionClick = (
    dashboardType: string,
    dashboardId: number,
    dashboardUrl: string,
  ) => {
    if (dashboardType === DashboardTypes.MAIN_DASHBOARD) {
      navigate(
        `${paths.ASSETS_LIST}/${selectedAsset?.id}/${
          selectedAsset && encodeURIComponent(selectedAsset.name)
        }${paths.DASHBOARD_PERFORMANCE}/${dashboardId}`,
      );
    } else if (dashboardType === DashboardTypes.OPC_DASHBOARD) {
      navigate(
        `${paths.ASSETS_LIST}/${selectedAsset?.id}/${
          selectedAsset && encodeURIComponent(selectedAsset.name)
        }${paths.DASHBOARD_OPC}/${dashboardId}`,
      );
    } else window.open(dashboardUrl, 'toolbar=no,resizable=yes,location=yes');
  };

  const columns = [
    {
      headerName: t(`page.asset.table-name`),
      field: 'name',
      minWidth: 600,
      renderCell: (e: any) => {
        if (!data) return <Loader />;

        const hasDescendants = data?.filter((x) => x.parentAssetId === e.row.id).length > 0;
        const isOpen = isAssetOpen(e.row.id);
        const offset = e.row.level * 24;
        const offsetWithPlus = e.row.level * 25;

        if (!hasDescendants)
          return <div style={{ paddingLeft: 32 + offset + 'px' }}>{e.row.name}</div>;

        return (
          <>
            <Button
              onClick={() => handleGrouping(e.row)}
              style={{ minWidth: '16px', paddingLeft: 0 + offsetWithPlus + 'px' }}
            >
              {isOpen ? <IndeterminateCheckBoxOutlinedIcon /> : <AddBoxOutlinedIcon />}
            </Button>
            {e.row.name}
          </>
        );
      },
    },
    { headerName: t(`page.asset.table-group`), field: 'groupName', width: 250 },
    {
      headerName: ' ',
      field: 'id',
      sortable: false,
      disableColumnMenu: true,
      renderCell: (e: any) => {
        return (
          <>
            <Tooltip title={t(`page.asset.tooltip-button-open-modal`)}>
              <Button onClick={() => handleOpen(e.row)}>
                <DashboardIcon />
              </Button>
            </Tooltip>
          </>
        );
      },
    },
  ];

  if (isError) return <ApiError />;
  if (isLoading || !localData) return <Loader />;

  return (
    <>
      {dashboardData && selectedAsset && !dashboardDataFetching && (
        <AssetDashboardMenu
          dashboardData={dashboardData}
          handleAssetButtonActionClick={handleAssetButtonActionClick}
          open={open}
          setOpen={setOpen}
          selectedAsset={selectedAsset}
        />
      )}
      <AssetListGroupSearch
        setSelectedGroup={setSelectedGroup}
        setStorageGroup={setStorageGroup}
        storageGroup={storageGroup}
      />
      <Grid item xs={12}>
        <div style={{ height: '60vh', width: '100%' }}>
          <DataGridTable data={localData} columns={columns} fulltextSearch={true} />
        </div>
      </Grid>
    </>
  );
}
